import {
  Stack,
  Checkbox,
  Group,
  TextInput,
  Textarea,
  NumberInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { CancelButton, SaveButton } from "../../../../-components/Buttons"
import {
  AddPersonalCalendarProfile,
  UpdatePersonalCalendarProfile,
} from "../../../../@client/Theasoft_ApiServer_WebCalendar"
import { ErrorAlert } from "../../../../blocks/errors/error-alert"
import { pathProxy } from "../../../../utils/forms-proxy"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { usePostMutation } from "../../../../typed-client/hooks/use-post-mutation"
import { DefaultModal } from "../../../../-components/default-modal"
import { useGetDatabaseRulesSuspense } from "@/orval/gen"
import { InfiniteUserMultiSelect } from "@components/InfiniteUserSelect"
import { useQueryGet } from "@/typed-client/hooks/use-query-get"

interface Props {
  profileId: number | null
  onClose: () => void
  addCalendarProfilePathProxy: ReturnType<
    typeof pathProxy<AddPersonalCalendarProfile>
  >
}

function EditCalendarProfileModalContent({
  profileId,
  onClose,
  addCalendarProfilePathProxy,
}: Props) {
  const { t } = useTranslation("app_server")
  const client = useQueryClient()
  const form = useForm<UpdatePersonalCalendarProfile>({})
  const { data: profile } = useQueryGet(
    "/api/WebCalendarConfig/GetUpdatePersonalCalendarProfile",
    { profileId: profileId! },
    {
      enabled: profileId != null,
    }
  )

  React.useEffect(() => {
    if (!!profile) {
      form.setInitialValues({
        description: profile.description || "",
        webCalendarProfileId: profile.webCalendarProfileId,
        name: profile.name,
        settings: profile.settings,
        isPublic: profile.isPublic,
        visibleForUserIds: profile.visibleForUserIds,
      })
      form.reset()
    }
  }, [profile])
  const updateProfile = usePostMutation(
    "/api/WebCalendarConfig/UpdatePersonalWebCalendarProfile",
    {
      onSuccess: () => {
        onClose()
        client.invalidateQueries({
          queryKey: ["/api/WebCalendar/get-profiles", null],
        })
      },
    }
  )

  const { data: databaseRoles } = useGetDatabaseRulesSuspense()
  const canCreatePublicWebCalendars = databaseRoles.includes(
    "CreatePublicWebCalendarProfiles"
  )

  return (
    <>
      {/* <Debug _show {...profile} /> */}
      <form onSubmit={form.onSubmit((values) => updateProfile.mutate(values))}>
        <Stack gap="xs">
          <Checkbox.Group
            label={t("AppointmentTypes")}
            description={t(
              "Calendar_Subscriptions_AppointmentTypesDescription"
            )}
          >
            {/* empty CheckboxGroup just for the label and description, else it would control the state (we want each checkbox to control a property) */}
            <div />
          </Checkbox.Group>
          <Group>
            <Checkbox
              label={t("Absences")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeAbsenceAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("Casts")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeCastAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("PossibleCast")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings
                  .includePossibleCastAppointments._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("DutyRosters")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings
                  .includeDutyRosterAppointments._PATH_,
                { type: "checkbox" }
              )}
            />
            <Checkbox
              label={t("Travels")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.includeTravelAppointments
                  ._PATH_,
                { type: "checkbox" }
              )}
            />
          </Group>
          <TextInput
            autoFocus
            label={t("Name")}
            data-autofocus
            placeholder={t("Calendar_Subscriptions_CreateModalNamePlaceholder")}
            {...form.getInputProps(addCalendarProfilePathProxy.name._PATH_)}
          />
          <Textarea
            label={t("Description")}
            placeholder={t(
              "Calendar_Subscriptions_CreateModalDescriptionPlaceholder"
            )}
            {...form.getInputProps(
              addCalendarProfilePathProxy.description._PATH_
            )}
          />
          <Group>
            <NumberInput
              label={t("NumberOfDaysInThePast")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.daysOffsetInPast._PATH_
              )}
            />
            <NumberInput
              label={t("NumberOfDaysInTheFuture")}
              {...form.getInputProps(
                addCalendarProfilePathProxy.settings.daysOffsetInFuture._PATH_
              )}
            />
          </Group>{" "}
          {canCreatePublicWebCalendars && (
            <>
              <Checkbox
                key={form.key("isPublic")}
                label={t("Public")}
                {...form.getInputProps("isPublic", { type: "checkbox" })}
              />
              {form.getValues().isPublic && (
                <InfiniteUserMultiSelect
                  value={form.getValues().visibleForUserIds?.map(String) ?? []}
                  onSelectionChange={(value) =>
                    form.setFieldValue("visibleForUserIds", value.map(Number))
                  }
                  filterOptions={{ userTypeFilter: "Groups" }}
                />
              )}
            </>
          )}
          <ErrorAlert error={updateProfile.error} />
          <Group mt="md">
            <CancelButton onClick={onClose} />
            <SaveButton loading={updateProfile.isPending} />
          </Group>
        </Stack>
      </form>
    </>
  )
}

export function EditCalendarProfileModal(props: Props) {
  const { data: profile } = useQueryGet(
    "/api/WebCalendarConfig/GetUpdatePersonalCalendarProfile",
    { profileId: props.profileId! },
    {
      enabled: props.profileId != null,
    }
  )
  const { t } = useTranslation("app_server")
  return (
    <DefaultModal
      size="lg"
      title={t("Calendar_Subscriptions_EditModalTitle")}
      opened={profile != null}
      onClose={props.onClose}
    >
      <EditCalendarProfileModalContent {...props} />
    </DefaultModal>
  )
}
